// Vuex module for Tally Reports (Print API)
// All network calls are centralized here per project guidelines.

import axios from 'axios'
import { Storage } from '@/Services/Storage'

/**
 * @typedef {Object} TallyReportDefinition
 * @property {string} name - Human readable name for the report
 * @property {'GET'} method - HTTP method (Print API uses GET for Tally)
 * @property {(db:string)=>string|String} path - Relative path builder: e.g. (DB)=>`tally/${DB}/sales`
 * @property {{name:string, required?:boolean, type?:string, sample?:any}[]} query - Querystring fields
 * @property {'xlsx'} [format] - Expected file extension
 */

/**
 * Catalog of Tally report endpoints from docs/print-api.yaml
 * Keep this in sync with resources/api.yaml and docs/print-api.yaml
 * Response type: application/octet-stream (Excel file)
 */
export const tallyCatalog = [
  {
    name: 'Sales and Sales Returns',
    method: 'GET',
    path: (DB) => `tally/${DB}/sales`,
    // Types added to support dynamic UI rendering if API adds more fields later
    query: [
      { name: 'from', required: true, type: 'date' },
      { name: 'to', required: true, type: 'date' },
      { name: 'refund', required: true, type: 'boolean', sample: false }
    ],
    format: 'xlsx'
  },
  {
    name: 'Purchases / Purchase Returns',
    method: 'GET',
    path: (DB) => `tally/${DB}/purchases`,
    query: [
      { name: 'from', required: true, type: 'date' },
      { name: 'to', required: true, type: 'date' },
      { name: 'refund', required: true, type: 'boolean', sample: false }
    ],
    format: 'xlsx'
  },
  {
    name: 'Customer Receipts',
    method: 'GET',
    path: (DB) => `tally/${DB}/customerReceipts`,
    query: [ { name: 'from', required: true, type: 'date' }, { name: 'to', required: true, type: 'date' } ],
    format: 'xlsx'
  },
  {
    name: 'Contra Entries',
    method: 'GET',
    path: (DB) => `tally/${DB}/contraEntries`,
    query: [ { name: 'from', required: true, type: 'date' }, { name: 'to', required: true, type: 'date' } ],
    format: 'xlsx'
  },
  {
    name: 'Supplier Payments',
    method: 'GET',
    path: (DB) => `tally/${DB}/supplierPayments`,
    query: [ { name: 'from', required: true, type: 'date' }, { name: 'to', required: true, type: 'date' } ],
    format: 'xlsx'
  },
  // NEW: Expenses
  {
    name: 'Expenses',
    method: 'GET',
    path: (DB) => `tally/${DB}/expenses`,
    query: [ { name: 'from', required: true, type: 'date' }, { name: 'to', required: true, type: 'date' } ],
    format: 'xlsx'
  }
]

export default {
  namespaced: true,
  state: () => ({
    catalog: tallyCatalog,
    loading: false,
    error: null,
  }),
  getters: {
    tallyCatalog: (s) => s.catalog,
    tallyLoading: (s) => s.loading,
    tallyError: (s) => s.error,
  },
  mutations: {
    SET_LOADING(state, v) { state.loading = !!v },
    SET_ERROR(state, e) { state.error = e },
  },
  actions: {
    /**
     * Returns the static in-app catalog. If you later wire a dynamic YAML parser,
     * keep this action in place for API consistency.
     */
    async fetchTallyCatalog({ state }) {
      return state.catalog
    },

    /**
     * Download a Tally report as a Blob
     * @param _
     * @param {{def:TallyReportDefinition, params:Object}} payload
     * @returns {Promise<Blob>}
     */
    async downloadTallyReport(_, { def, params }) {
      const base = window.printApi // Global Print API base per guidelines
      const storeDB = params?.DB || (await Storage.getCurrentStoreDB())
      const path = typeof def.path === 'function' ? def.path(storeDB) : def.path

      // Ensure boolean refund is handled correctly if present
      const q = { ...params }
      if (Object.prototype.hasOwnProperty.call(q, 'refund')) {
        q.refund = (q.refund === true || q.refund === 'true')
      }

      const config = {
        responseType: 'blob',
        headers: { 'Accept': 'application/octet-stream' },
        params: q,
      }

      const { data } = await axios.get(base + path, config)
      return data // Blob
    },
  },
}
